import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid2 as Grid,
    Paper,
    Typography,
} from "@mui/material";
import { type ReactNode, useMemo } from "react";

import MediaDocumentsListRaw from "@/components/Documents/MediaDocumentsListRaw.tsx";
import { durationUnitOptions } from "@/components/Forms/SubForms/AddEditTaskTempalteDialog.tsx";
import type { Media } from "@/queries/useFetchMediaForCase.ts";
import type { CaseTaskWithDueDate } from "@/types/caseTask.ts";
import dayjs from "dayjs";

type Props = {
    task: CaseTaskWithDueDate | null;
    open: boolean;
    handleClose: () => void;
};

const CaseTaskDetailsDialog = ({ task, open, handleClose }: Props): ReactNode => {
    const durationDisplay = useMemo(() => {
        if (task) {
            return durationUnitOptions.find(
                (option) => Number.parseInt(option.days) === task.duration,
            );
        }
        return null;
    }, [task]);

    return (
        <Box>
            <Dialog open={open} maxWidth="sm" fullWidth>
                <DialogTitle>Case Task Details</DialogTitle>
                <DialogContent dividers>
                    {task !== null && (
                        <Grid container spacing={2}>
                            <Grid size={6} sx={{ p: 1 }} container>
                                <Grid size={12} sx={{ p: 1 }}>
                                    <Box>
                                        <Typography
                                            sx={{ fontWeight: 800, display: "inline", mr: 1 }}
                                        >
                                            Name:
                                        </Typography>
                                        <Typography sx={{ display: "inline" }}>
                                            {task.name}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography
                                            sx={{ fontWeight: 800, display: "inline", mr: 1 }}
                                        >
                                            Due:
                                        </Typography>
                                        <Typography sx={{ display: "inline" }}>
                                            {task.duration === 0
                                                ? "N/A"
                                                : task.dueDate.toLocaleDateString()}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography
                                            sx={{ fontWeight: 800, display: "inline", mr: 1 }}
                                        >
                                            Description:
                                        </Typography>
                                        <Typography sx={{ display: "inline" }}>
                                            {task.description}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                            <Grid size={6} sx={{ p: 1 }}>
                                {task.case && (
                                    <Paper>
                                        <Grid size={12} sx={{ p: 1 }}>
                                            <Box>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 800,
                                                        display: "inline",
                                                        mr: 1,
                                                    }}
                                                >
                                                    Case #:
                                                </Typography>
                                                <Typography sx={{ display: "inline" }}>
                                                    {task.case.id}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 800,
                                                        display: "inline",
                                                        mr: 1,
                                                    }}
                                                >
                                                    Client Name:
                                                </Typography>
                                                <Typography sx={{ display: "inline" }}>
                                                    {task.case.clientName}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 800,
                                                        display: "inline",
                                                        mr: 1,
                                                    }}
                                                >
                                                    Important Notes:
                                                </Typography>
                                                <Typography sx={{ display: "inline" }}>
                                                    {task.case.caseName}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Paper>
                                )}
                            </Grid>
                            {task?.notes.length > 0 && (
                                <Grid size={12} sx={{ p: 1, backgroundColor: "#efefef" }}>
                                    <Grid size={12} sx={{ px: 1, mb: 1, fontWeight: 800 }}>
                                        Task Notes:
                                    </Grid>
                                    {task.notes.map((note) => {
                                        let title = `${note.noteBy} - ${dayjs(
                                            note.metaModifiedWhen,
                                        ).format("MMM D YYYY - hh:mma")}`;

                                        if (note.tag) {
                                            title += ` - ${note.tag}`;
                                        }

                                        const media: Media[] = note.media;

                                        return (
                                            <Grid
                                                key={`note-${note.idUUID}`}
                                                container
                                                size={12}
                                                sx={{ p: 0, m: 0 }}
                                            >
                                                <Grid size={12} sx={{ px: 1 }}>
                                                    <Typography sx={{ mr: 1 }}>
                                                        {note.note}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 800,
                                                            mr: 1,
                                                            fontSize: "12px",
                                                        }}
                                                    >
                                                        {title}
                                                    </Typography>
                                                    {media.length > 0 && (
                                                        <MediaDocumentsListRaw
                                                            mediaArray={media}
                                                            showDelete={false}
                                                        />
                                                    )}
                                                    {media.length === 0 && (
                                                        <Divider sx={{ mb: 2 }} />
                                                    )}
                                                </Grid>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            )}
                            <Grid size={4} sx={{ p: 1 }}>
                                <Typography sx={{ fontWeight: 800, mr: 1 }}>
                                    Completed By:
                                </Typography>
                                <Typography>{task.completedBy}</Typography>
                            </Grid>
                            <Grid size={4} sx={{ p: 1 }}>
                                <Typography sx={{ fontWeight: 800, mr: 1 }}>
                                    Assigned Date:
                                </Typography>
                                <Typography>{task.metaCreatedWhen.toLocaleDateString()}</Typography>
                            </Grid>
                            <Grid size={4} sx={{ p: 1 }}>
                                <Typography sx={{ fontWeight: 800, mr: 1 }}>
                                    Completed Date:
                                </Typography>
                                <Typography>{task.dateCompleted?.toLocaleDateString()}</Typography>
                            </Grid>
                            <Grid size={12} sx={{ p: 1 }}>
                                <Typography sx={{ fontWeight: 800, mr: 1 }}>
                                    Task Duration:
                                </Typography>
                                <Typography>{durationDisplay?.name}</Typography>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CaseTaskDetailsDialog;
